import {
  Component,
  lazy,
  Suspense,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react'
import { AlertTriangle, ArrowLeft, LoaderCircle } from 'lucide-react'
import type { GraphEditor } from './GraphEditor'

type BoundaryProps = {
  children: ReactNode
  onReturnToWriting: () => void
  onSaveAndReload: () => Promise<void>
}

class MapBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  render() {
    if (!this.state.failed) return this.props.children
    return (
      <div className="map-unavailable" role="alert">
        <AlertTriangle size={24} />
        <h2>The story map couldn’t open.</h2>
        <p>
          Your draft is still available. Reconnect, then save and reload to open
          the map.
        </p>
        <button
          type="button"
          className="button"
          onClick={this.props.onReturnToWriting}
        >
          <ArrowLeft size={15} /> Return to Write
        </button>
        <button
          type="button"
          className="button button--primary"
          onClick={() => void this.props.onSaveAndReload()}
        >
          Save and reload
        </button>
      </div>
    )
  }
}

export function GraphWorkspace({
  onReturnToWriting,
  onSaveAndReload,
  ...props
}: ComponentProps<typeof GraphEditor> & Omit<BoundaryProps, 'children'>) {
  // Keep loading and rendering failures inside the map, preserving the writing session.
  const [Editor] = useState(() =>
    lazy(() =>
      import('./GraphEditor').then((module) => ({
        default: module.GraphEditor,
      })),
    ),
  )
  return (
    <MapBoundary
      onReturnToWriting={onReturnToWriting}
      onSaveAndReload={onSaveAndReload}
    >
      <Suspense
        fallback={
          <div className="workspace-loading" role="status">
            <LoaderCircle size={20} className="spin" /> Opening your story map…
          </div>
        }
      >
        <Editor {...props} />
      </Suspense>
    </MapBoundary>
  )
}
